import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rewardApi } from '../../../api/reward.api';
import { AdminExchangeItem } from '../../../types/Reward.api.type';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';

/** 상태 색상 매핑 */
const STATUS_COLORS: Record<string, string> = {
  SUCCESS: 'bg-green-100 text-green-700',
  PENDING: 'bg-yellow-100 text-yellow-700',
  FAILED: 'bg-red-100 text-red-700',
};

/** 상태 한국어 매핑 */
const STATUS_LABELS: Record<string, string> = {
  SUCCESS: '성공',
  PENDING: '대기',
  FAILED: '실패',
};

export const ExchangeHistoryPanel = () => {
  const [page, setPage] = useState(0);
  const [userIdInput, setUserIdInput] = useState('');
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const pageSize = 20;

  const handleSearch = () => {
    const parsed = userIdInput.trim() ? Number(userIdInput.trim()) : undefined;
    setUserId(parsed && !isNaN(parsed) ? parsed : undefined);
    setPage(0);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['adminExchangeHistory', page, userId],
    queryFn: () => rewardApi.getExchangeHistory(page, pageSize, userId).then(res => res.data.results),
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString('ko-KR') + '원';
  };

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700">토스 포인트 지급 내역</h3>
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder="유저 ID"
              value={userIdInput}
              onChange={e => setUserIdInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="w-28 px-2 py-1 text-sm border rounded"
            />
            <Button variant="outline" size="sm" onClick={handleSearch}>
              <Search className="w-3 h-3" />
            </Button>
            {userId != null && (
              <Button variant="ghost" size="sm" onClick={() => { setUserIdInput(''); setUserId(undefined); setPage(0); }}>
                초기화
              </Button>
            )}
          </div>
        </div>
        {isLoading ? (
          <div className="text-center py-8 text-gray-400">로딩 중...</div>
        ) : !data?.content?.length ? (
          <div className="text-center py-8 text-gray-400">지급 내역이 없습니다.</div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-2 pr-3">ID</th>
                    <th className="pb-2 pr-3">유저</th>
                    <th className="pb-2 pr-3">포인트</th>
                    <th className="pb-2 pr-3">토스포인트</th>
                    <th className="pb-2 pr-3">광고</th>
                    <th className="pb-2 pr-3">상태</th>
                    <th className="pb-2 pr-3">에러</th>
                    <th className="pb-2">날짜</th>
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((item: AdminExchangeItem) => (
                    <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 pr-3 text-gray-400">{item.id}</td>
                      <td className="py-2 pr-3">{item.nickname}</td>
                      <td className="py-2 pr-3 font-medium">{item.pointAmount}개</td>
                      <td className="py-2 pr-3 font-medium text-blue-600">{formatAmount(item.tossAmount)}</td>
                      <td className="py-2 pr-3">
                        {item.adWatched ? (
                          <Badge variant="outline" className="text-xs text-green-600">시청</Badge>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[item.status] ?? 'bg-gray-100 text-gray-500'}`}>
                          {STATUS_LABELS[item.status] ?? item.status}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs text-red-500 max-w-[200px] truncate" title={item.errorMessage ?? undefined}>
                        {item.status === 'FAILED' && item.errorMessage ? item.errorMessage : '-'}
                      </td>
                      <td className="py-2 text-gray-400 text-xs">{formatDate(item.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden space-y-3">
              {data.content.map((item: AdminExchangeItem) => (
                <div key={item.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{item.nickname}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[item.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {STATUS_LABELS[item.status] ?? item.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{item.pointAmount}개 → {formatAmount(item.tossAmount)}</span>
                    {item.adWatched && <Badge variant="outline" className="text-xs text-green-600">광고</Badge>}
                  </div>
                  {item.status === 'FAILED' && item.errorMessage && (
                    <div className="text-xs text-red-500">{item.errorMessage}</div>
                  )}
                  <div className="text-xs text-gray-400">{formatDate(item.createdAt)}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t">
              <span className="text-xs text-gray-500">총 {data.totalElements}건</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-600">{page + 1} / {data.totalPages || 1}</span>
                <Button variant="outline" size="sm" disabled={page >= (data.totalPages || 1) - 1} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};
